<template>
  <div class="color-picker d-flex flex-wrap align-items-end">
    <b-form-group
      class="mb-0 mr-3"
      :label="label"
      :label-for="`${id}-preset`"
      label-class="vb-label"
    >
      <!-- vb-select snapshots its value in data(): the key makes it re-read when
           the parent changes `value` on its own, instead of ignoring it. Keyed on
           the preset so debounced custom colors don't remount it -->
      <vb-select
        :key="`${id}:${preset}`"
        :vb_options="{
          id: `${id}-preset`,
          options: select_options,
          value: preset,
        }"
        @change="on_preset_change"
      />
    </b-form-group>

    <b-form-group
      v-if="is_custom"
      class="mb-0 mr-3"
      :label="custom_label"
      :label-for="`${id}-custom`"
      label-class="vb-label"
    >
      <b-form-input
        :id="`${id}-custom`"
        class="color-picker__input"
        type="color"
        :value="custom_ui_color"
        :debounce="debounce"
        @update="on_custom_change"
      />
    </b-form-group>
  </div>
</template>

<script>
  const CUSTOM_PRESET = 'custom';

  export default {
    name: 'color-picker',

    model: {
      prop: 'value',
      event: 'change',
    },

    props: {
      // resolved color: either one of the preset values or the custom color
      value: {
        type: String,
        default: '',
      },
      // [{ value, text }], without the custom entry: this component appends it
      options: {
        type: Array,
        default: () => [],
      },
      // base for the ids of the two controls, must be unique in the page
      id: {
        type: String,
        required: true,
      },
      label: {
        type: String,
        default: 'Colore',
      },
      custom_label: {
        type: String,
        default: 'Colore personalizzato',
      },
      custom_text: {
        type: String,
        default: 'Custom…',
      },
      // color inputs fire on every drag step, so the value update is debounced.
      // Committing the picker still applies right away: bootstrap-vue forces the
      // update on the change event, bypassing the debounce.
      // NB: only the `update` (v-model) event is debounced, `input` fires on
      // every keystroke regardless, hence the @update listener above
      debounce: {
        type: Number,
        default: 300,
      },
      default_custom_color: {
        type: String,
        default: '#0d6efd',
      },
    },

    data() {
      return {
        // last color picked by hand, so switching back to custom restores it
        picked_custom_color: this.default_custom_color,
      };
    },

    computed: {
      select_options() {
        return [...this.options, { value: CUSTOM_PRESET, text: this.custom_text }];
      },

      // a value that matches no preset can only come from the color input
      is_custom() {
        if (!this.value) {
          return false;
        }
        return !this.options.some((option) => option.value === this.value);
      },

      preset() {
        if (this.is_custom) {
          return CUSTOM_PRESET;
        }
        return this.value;
      },

      // while custom, the incoming value wins: keeps pickers sharing a value in sync
      custom_ui_color() {
        if (this.is_custom) {
          return this.value;
        }
        return this.picked_custom_color;
      },
    },

    methods: {
      on_preset_change(preset) {
        if (preset === CUSTOM_PRESET) {
          this.$emit('change', this.picked_custom_color);
          return;
        }
        this.$emit('change', preset);
      },

      on_custom_change(color) {
        this.picked_custom_color = color;
        this.$emit('change', color);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .color-picker {
    &__input {
      padding: 4px;
    }
  }
</style>
